import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Flex } from '@mantine/core';
import type { CycleTimeHistogramBucket } from '../../../../api/agent-miner-client/agent-miner-client.type';
import { LineChartLegendItem } from '../../../../components/line-chart/line-chart-legend-item';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { skyLight } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import { hasDataForItem } from '../../components/line-chart/line-chart.helpers';
import { useItemColors } from '../../components/line-chart/line-chart.hooks';
import type { LineItem } from '../../components/line-chart/line-chart.type';
import { findBucketForMedian } from './main-section.helpers';

const ALL_SERIES: LineItem[] = [
  { id: 'agent_count', name: 'Agent' },
  { id: 'non_agent_count', name: 'Non-Agent' },
];

type Props = {
  histogram: CycleTimeHistogramBucket[];
  agentMedian: number | null;
  nonAgentMedian: number | null;
};

const DashedLineMarker = ({ color, size = 16 }: { color: string; size?: number }) => {
  return (
    <Box style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <line x1="0" y1="8" x2="16" y2="8" stroke={color} strokeWidth="2" strokeDasharray="3 2" />
      </svg>
    </Box>
  );
};

export const HistogramChart = ({ histogram, agentMedian, nonAgentMedian }: Props) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const agentVisibleSeries = useProcessAnalysisStore((state) => state.agentVisibleSeries);

  const selectedItems = ALL_SERIES.filter((series) => agentVisibleSeries[series.id] ?? true);
  const itemColors = useItemColors(selectedItems);

  const isHighlighted = (key: string) => hoveredItem === null || hoveredItem === key;

  const agentMedianBucket = agentMedian !== null ? findBucketForMedian(agentMedian, histogram) : null;
  const nonAgentMedianBucket = nonAgentMedian !== null ? findBucketForMedian(nonAgentMedian, histogram) : null;

  const agentVisible = agentMedianBucket !== null && (agentVisibleSeries.agent_count ?? true);
  const nonAgentVisible = nonAgentMedianBucket !== null && (agentVisibleSeries.non_agent_count ?? true);

  const chartData = histogram.map((bucket) => ({
    date: bucket.bucket_days,
    rawDate: bucket.bucket_days,
    agent_count: bucket.agent_count,
    non_agent_count: bucket.non_agent_count,
  }));

  return (
    <div style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <BarChart data={histogram} margin={{ top: 20, right: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
          <XAxis
            dataKey="bucket_days"
            tick={{ fontSize: 12 }}
            label={{ value: 'Days', position: 'insideBottom', offset: -5 }}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            label={{ value: 'Tasks', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' }, offset: 10 }}
          />
          <Tooltip />
          {selectedItems.map((item) => (
            <Bar
              key={item.id}
              dataKey={item.id}
              name={item.name}
              fill={itemColors[item.id]}
              opacity={isHighlighted(item.id) ? 1 : 0.3}
            />
          ))}
          {agentVisible && (
            <ReferenceLine
              x={agentMedianBucket}
              stroke={itemColors['agent_count']}
              strokeDasharray="5 5"
              strokeWidth={2}
            />
          )}
          {nonAgentVisible && (
            <ReferenceLine
              x={nonAgentMedianBucket}
              stroke={itemColors['non_agent_count']}
              strokeDasharray="5 5"
              strokeWidth={2}
            />
          )}
          <Legend
            content={
              <Flex justify="center" align="center" gap="md" wrap="wrap" h="100%">
                {selectedItems.map((item) => (
                  <LineChartLegendItem
                    key={item.id}
                    value={item.id}
                    label={item.name}
                    color={itemColors[item.id]}
                    isHovered={hoveredItem === null || hoveredItem === item.id}
                    hasData={hasDataForItem(chartData, item.id)}
                    onMouseEnter={setHoveredItem}
                    onMouseLeave={() => setHoveredItem(null)}
                  />
                ))}
                {agentVisible && (
                  <Flex align="center" gap={4}>
                    <DashedLineMarker color={itemColors['agent_count']} />
                    <Text size="tiny">Agent Median: {agentMedian?.toFixed(1)}d</Text>
                  </Flex>
                )}
                {nonAgentVisible && (
                  <Flex align="center" gap={4}>
                    <DashedLineMarker color={itemColors['non_agent_count']} />
                    <Text size="tiny">Non-Agent Median: {nonAgentMedian?.toFixed(1)}d</Text>
                  </Flex>
                )}
              </Flex>
            }
            verticalAlign="bottom"
            height={agentVisible || nonAgentVisible ? 52 : 36}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
